import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CabinetFile, GroupedFiles, Folder, FilePurpose, FileCabinetDestination } from '../../types/file.types';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { CIPXScrollableContentComponent } from '@sbd-doug-frontend/shared/lib/ui';
import { environment } from '@sbd-doug-frontend/app/environments/environment';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { CIPXPillSwitcherComponent, CIPXPillSwitcherOption } from '../pill-switcher/pill-switcher.component';
import { HttpClient } from '@angular/common/http';
import { FileUploadService } from '../../ui/file-upload/file-upload.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { UserService } from '@sbd-doug-frontend/app/core/user/user.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FileUploaderModalComponent } from '@sbd-doug-frontend/app/modules/file-uploader/file-uploader-modal.component';
import { FileUploadConfig } from '../../ui/file-upload/file-upload.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';

@Component({
  selector: 'sbd-doug-frontend-file-cabinet',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatExpansionModule,
    MatListModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    MatButtonToggleModule,
    MatTreeModule,
    CIPXScrollableContentComponent,
    CIPXPillSwitcherComponent,
    MatDialogModule,
  ],
  templateUrl: './file-cabinet.component.html',
  styleUrls: ['./file-cabinet.component.scss'],
})
export class FileCabinetComponent implements OnInit {
  private _httpClient = inject(HttpClient);
  private _fileUploadService = inject(FileUploadService);
  private _cathyService = inject(CathyService);
  private _userService = inject(UserService);
  private _dialog = inject(MatDialog);
  private _fuseConfirmationService = inject(FuseConfirmationService);

  @Input() accountId: number | undefined;
  @Input() set files(value: any | undefined) {
    // Handle the wrapper if it exists
    if (value && value.file_cabinet) {
      this._files = value.file_cabinet;
    } else {
      this._files = value;
    }
    this.updateDataSource();

    if (this.path && this.path.length > 0) {
      this.restorePath();
    } else {
      this.resetToRoot();
    }
  }
  get files(): GroupedFiles | undefined {
    return this._files;
  }
  private _files: GroupedFiles | undefined;
  @Output() refreshRequested = new EventEmitter<void>();

  path: { name: string; data: Folder | GroupedFiles }[] = [];
  currentFolder: { name: string; data: Folder | GroupedFiles } | undefined;

  viewType: 'tile' | 'tree' = 'tile';

  viewOptions: CIPXPillSwitcherOption[] = [
    { id: 'tile', label: '', tooltip:'Tile', icon: 'heroicons_outline:view-grid' },
    { id: 'tree', label: '', tooltip: 'Tree', icon: 'heroicons_outline:view-list' },
  ];

  treeControl = new NestedTreeControl<any>((node) => this.getChildren(node));
  dataSource = new MatTreeNestedDataSource<any>();

  filePurposes: FilePurpose[] = [];
  selectedFolder: any = null;
  isDragging = false;

  /** File date sort: newest-first or oldest-first. Deleted files always sort first. */
  fileSortOrder: 'newest' | 'oldest' =
    (localStorage.getItem('sbd-file-cabinet-file-sort') as 'newest' | 'oldest') || 'newest';

  ngOnInit(): void {
    this.resetToRoot();
    this.loadFilePurposes();
  }

  loadFilePurposes(): void {
    this._fileUploadService.getFilePurposes().subscribe(purposes => {
      this.filePurposes = purposes.filter(p => {
        const isSystem = p.is_system === true || p.is_system === 1 || p.is_system === '1';
        const isOther = p.file_purpose_code === 'other';
        const isActive = p.is_active === undefined || p.is_active === true || p.is_active === 1 || p.is_active === '1';
        return (isSystem || isOther) && isActive;
      });
    });
  }

  updateDataSource(): void {
    const roots = this.getFolders(this._files);
    this.dataSource.data = roots;

    // Expand top-level folders so the tree is useful immediately on mobile/desktop
    queueMicrotask(() => {
      for (const folder of roots) {
        if (this.hasChild(0, folder) && !this.treeControl.isExpanded(folder)) {
          this.treeControl.expand(folder);
        }
      }
    });
  }

  hasChild = (_: number, node: any) => {
    if (!this.isFolder(node)) {
      return false;
    }
    const folders = this.getFolders(node);
    const files = this.getFiles(node);
    return folders.length > 0 || files.length > 0;
  };

  getChildren(node: any): any[] {
    const folders = this.getFolders(node);
    const files = this.getFiles(node);
    return [...folders, ...files];
  }

  getFolderItemCount(folder: Folder | any): number {
    if (!folder) {
      return 0;
    }
    // getFiles already hides soft-deleted files for users without manage permission
    return this.getFolders(folder).length + this.getFiles(folder).length;
  }

  /** Display name for a folder (label preferred). */
  getFolderDisplayName(folder: Folder | any): string {
    return String(
      folder?.label || folder?.code || folder?.scope_key || folder?.file_purpose_code || 'Folder',
    ).trim();
  }

  /** Singular/plural item count label (0 items, 1 item, 2 items). */
  formatItemCount(count: number): string {
    const n = Number(count) || 0;
    return n === 1 ? '1 item' : `${n} items`;
  }

  isFolder(node: any): boolean {
    // Files always have a file_id; folders never do.
    if (!node || node.file_id) {
      return false;
    }
    return (
      node.folders !== undefined ||
      node.subfolders !== undefined ||
      node.label !== undefined ||
      node.files !== undefined ||
      node.file !== undefined
    );
  }

  isBlankFile(file: CabinetFile): boolean {
    return (!file.file_name && !file.file_friendly_name && !file.file_size) || !file.file_id;
  }

  resetToRoot(): void {
    this.path = [];
    if (this._files) {
      this.currentFolder = { name: 'Home', data: this._files };
    } else {
      this.currentFolder = { name: 'Home', data: { folders: [] } };
    }
    this.selectedFolder = null;
  }

  private restorePath(): void {
    if (!this._files) {
      this.resetToRoot();
      return;
    }

    let currentData: any = this._files;
    const newPath: { name: string; data: Folder | GroupedFiles }[] = [];

    for (const segment of this.path) {
      const folders = this.getFolders(currentData);
      const found = folders.find((f) => f.label === segment.name);
      if (found) {
        newPath.push({ name: found.label, data: found });
        currentData = found;
      } else {
        // Path broken (folder might have been deleted or renamed)
        break;
      }
    }

    if (newPath.length > 0) {
      this.path = newPath;
      this.currentFolder = this.path[this.path.length - 1];
      // Try to restore selectedFolder if it matches current folder or its parents
      if (this.selectedFolder) {
        const foundSelected = newPath.find(p => p.name === this.selectedFolder.label);
        if (foundSelected) {
          this.selectedFolder = foundSelected.data;
        } else {
          this.selectedFolder = this.currentFolder.data;
        }
      }
    } else {
      this.resetToRoot();
    }
  }

  isGroupedFiles(data: any): data is GroupedFiles {
    return data === this._files;
  }

  getFolders(data: any): Folder[] {
    if (!data) {
      return [];
    }

    let folders: Folder[] = [];
    if (this.isGroupedFiles(data)) {
      if (Array.isArray(data)) {
        folders = data;
      } else if (data.folders && Array.isArray(data.folders)) {
        folders = data.folders;
      } else {
        // If it's an object of folders (either data itself or data.folders)
        const folderSource: any = data.folders || data;
        folders = Object.keys(folderSource)
          .filter((key) => !['total_files_count', 'folders'].includes(key))
          .map((key) => {
            const item = folderSource[key];
            if (this.isFolder(item)) {
              return {
                ...item,
                label: item.label || key,
                code: item.code || item.scope_key || item.file_purpose_code || key,
              };
            }
            return null;
          })
          .filter((f) => f !== null) as Folder[];
      }
    } else {
      folders = data.subfolders || [];
    }

    return folders;
  }

  getFiles(data: any): CabinetFile[] {
    if (!data || this.isGroupedFiles(data)) {
      return [];
    }
    const files = data.files || data.file || [];
    const mapped = files
      .map((f: any) => (f.file ? f.file : f))
      .filter((f: any) => !this.isBlankFile(f))
      // View-only users should not see (or count) soft-deleted files
      .filter((f: any) => this.canManageFiles || !this.isDeleted(f)) as CabinetFile[];

    return [...mapped].sort((a, b) => {
      // Deleted files first (managers only; already filtered for others)
      const aDeleted = this.isDeleted(a) ? 0 : 1;
      const bDeleted = this.isDeleted(b) ? 0 : 1;
      if (aDeleted !== bDeleted) {
        return aDeleted - bDeleted;
      }

      const aTime = a.ctime ? new Date(a.ctime).getTime() : 0;
      const bTime = b.ctime ? new Date(b.ctime).getTime() : 0;
      return this.fileSortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }

  /** Switch tile/list view; list view always starts at Home (no grid breadcrumbs). */
  setViewType(type: 'tile' | 'tree'): void {
    if (this.viewType === type) {
      return;
    }
    this.viewType = type;
    // Leaving a grid folder must not keep breadcrumbs when entering list view
    this.resetToRoot();
  }

  setFileSortOrder(order: 'newest' | 'oldest'): void {
    this.fileSortOrder = order;
    localStorage.setItem('sbd-file-cabinet-file-sort', order);
    this.updateDataSource();
  }

  isDeleted(file: CabinetFile): boolean {
    return file?.related_file_display_order == 0;
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined || isNaN(Number(bytes))) {
      return '';
    }
    const size = Number(bytes);
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  getFileDisplayName(file: CabinetFile): string {
    const name = file?.file_friendly_name || file?.file_provided_name || file?.file_name || 'Untitled';
    const ext = (file?.file_extension || '').trim();

    if (ext) {
      const suffix = `.${ext}`;
      if (name.toLowerCase().endsWith(suffix.toLowerCase())) {
        return name.slice(0, -suffix.length) || name;
      }
    }

    // Fallback: strip a trailing extension segment when present
    const lastDot = name.lastIndexOf('.');
    if (lastDot > 0 && lastDot < name.length - 1 && !name.slice(lastDot + 1).includes(' ')) {
      return name.slice(0, lastDot) || name;
    }

    return name;
  }

  getExtensionColorClass(ext?: string | null): string {
    const e = (ext || '').toUpperCase();
    if (['PDF', 'HTML'].includes(e)) {
      return 'bg-red-600';
    }
    if (['DOC', 'DOCX'].includes(e)) {
      return 'bg-blue-600';
    }
    if (['XLS', 'XLSX'].includes(e)) {
      return 'bg-green-600';
    }
    if (e === 'TXT') {
      return 'bg-gray-600';
    }
    if (['JPG', 'JPEG', 'PNG', 'GIF'].includes(e)) {
      return 'bg-amber-600';
    }
    // Unsupported / unknown extensions
    return 'bg-black';
  }

  navigateToFolder(folder: Folder): void {
    const name = this.getFolderDisplayName(folder);
    this.path.push({ name, data: folder });
    this.currentFolder = { name, data: folder };
    this.selectedFolder = folder;
  }

  navigateBack(index: number): void {
    if (index === -1) {
      this.resetToRoot();
    } else {
      this.path = this.path.slice(0, index + 1);
      this.currentFolder = this.path[this.path.length - 1];
    }
    this.selectedFolder = null;
  }

  selectFolder(folder: any, expand: boolean = false): void {
    this.selectedFolder = folder;
    if (expand && this.hasChild(0, folder) && !this.treeControl.isExpanded(folder)) {
      this.treeControl.expand(folder);
    }
  }

  viewFile(file: CabinetFile): void {
    window.open(`${environment.sbdBaseUrl}/admin/files/view/?file_id=${file.file_id}`, '_blank');
  }

  downloadFile(file: CabinetFile): void {
    window.open(`${environment.sbdBaseUrl}/admin/files/download/?file_id=${file.file_id}`, '_blank');
  }


  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.file_id || item.label || item.id || index;
  }

  get canManageFiles(): boolean {
    return this._userService.currentAdmin()?.permissions?.some(p => p.toLowerCase() === 'accountfilemanager') || false;
  }

  /** True when the user has navigated into a folder (not Home/root). */
  get isInFolder(): boolean {
    return Array.isArray(this.path) && this.path.length > 0;
  }

  deleteFile(file: CabinetFile): void {
    if (!this.accountId || this.isDeleted(file)) return;

    const confirmation = this._fuseConfirmationService.open({
      title: 'Delete File',
      message: 'Are you sure you want to delete this file?',
      actions: {
        confirm: {
          label: 'Delete',
          color: 'warn',
        },
      },
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        const url = `${environment.apiBaseUrl}/api/v4/file-cabinet/${this.accountId}/files/${file.file_id}`;
        this._httpClient.get(url).subscribe({
          next: () => {
            this._cathyService.success('File deleted successfully');
            this.refreshRequested.emit();
          },
          error: (err) => {
            this._cathyService.error('Failed to delete file: ' + (err.error?.message || err.message));
          },
        });
      }
    });
  }

  restoreFile(file: CabinetFile): void {
    if (!this.accountId || !this.isDeleted(file)) return;

    const confirmation = this._fuseConfirmationService.open({
      title: 'Undo Deletion',
      message: 'Are you sure you want to restore this file?',
      actions: {
        confirm: {
          label: 'Restore',
          color: 'primary',
        },
      },
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        const url = `${environment.apiBaseUrl}/api/v4/file-cabinet/${this.accountId}/files/${file.file_id}/restore`;
        this._httpClient.get(url).subscribe({
          next: () => {
            this._cathyService.success('File restored successfully');
            this.refreshRequested.emit();
          },
          error: (err) => {
            this._cathyService.error('Failed to restore file: ' + (err.error?.message || err.message));
          },
        });
      }
    });
  }

  /**
   * Root-level folder destinations shown when uploading from Home.
   */
  getRootDestinations(): FileCabinetDestination[] {
    return this.getFolders(this._files)
      .map((folder) => {
        const code = String(folder?.code || folder?.scope_key || folder?.file_purpose_code || '').trim();
        const label = String(folder?.label || code || '').trim();
        if (!code || !label) {
          return null;
        }
        return { label, code };
      })
      .filter((d): d is FileCabinetDestination => !!d);
  }

  /**
   * Active folder context from navigation path (deepest folder with a code wins).
   */
  getActiveFolderContext(): { label: string; code?: string } | null {
    if (!this.isInFolder || !this.path?.length) {
      return null;
    }

    for (let i = this.path.length - 1; i >= 0; i--) {
      const segment = this.path[i];
      const data = segment?.data as Folder | any;
      const code = String(data?.code || data?.scope_key || data?.file_purpose_code || '').trim();
      const label = String(data?.label || segment?.name || '').trim();
      if (code || label) {
        return { label: label || code, code: code || undefined };
      }
    }

    return null;
  }

  /**
   * Resolve a file purpose from a folder/category code or label.
   * Folders in the cabinet are purpose categories (e.g. ap_payments → receipt).
   */
  resolvePurposeForFolder(folder: { code?: string; label?: string } | null | undefined): FilePurpose | undefined {
    if (!folder || !this.filePurposes?.length) {
      return undefined;
    }

    const code = String(folder.code || '').trim().toLowerCase();
    const label = String(folder.label || '').trim().toLowerCase();

    const byCategory = this.filePurposes.filter((p) => {
      const catCode = String(p.file_purpose_category_code || '').trim().toLowerCase();
      const catLabel = String(p.file_purpose_category_label || '').trim().toLowerCase();
      return (code && catCode === code) || (label && catLabel === label);
    });

    if (byCategory.length === 1) {
      return byCategory[0];
    }

    if (byCategory.length > 1) {
      // Prefer a purpose whose own code matches the folder code (e.g. receipt under ap_payments when unique)
      const exactCode = byCategory.find((p) => String(p.file_purpose_code || '').toLowerCase() === code);
      if (exactCode) {
        return exactCode;
      }
      // Prefer label match on purpose
      const exactLabel = byCategory.find((p) => String(p.file_purpose_label || '').toLowerCase() === label);
      if (exactLabel) {
        return exactLabel;
      }
      return byCategory[0];
    }

    // Fallbacks: purpose code/label equals folder code/label, then "other"
    return (
      this.filePurposes.find((p) => String(p.file_purpose_code || '').toLowerCase() === code) ||
      this.filePurposes.find((p) => String(p.file_purpose_label || '').toLowerCase() === label) ||
      this.filePurposes.find((p) => String(p.file_purpose_code || '').toLowerCase() === 'other')
    );
  }

  openUploadDialog(files?: FileList | File[]): void {
    if (!this.accountId) {
      this._cathyService.error('Account is required to upload files.');
      return;
    }

    // Open immediately — modal loads purposes itself when the list is empty.
    // Do not wait on ui-lookups here or a slow/hung request will make Upload appear broken.
    const queuedFiles = files ? Array.from(files as ArrayLike<File>) : [];
    const atHome = !this.isInFolder;
    const folderContext = this.getActiveFolderContext();
    const resolvedPurpose = atHome ? undefined : this.resolvePurposeForFolder(folderContext);
    const destinations = atHome ? this.getRootDestinations() : [];

    const dialogData = {
      context: 'cipxaccount', // Using account context as seen in backend (Account::SCOPE_KEY)
      id: this.accountId,
      endpoint: `${environment.apiBaseUrl}/api/v4/file-cabinet/${this.accountId}/files`,
      uploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        horizontal: true,
      },
      title: atHome ? 'Upload Files' : `Upload to ${folderContext?.label || 'Folder'}`,
      // File Cabinet: purpose is assumed from folder. At Home, ask where to save.
      requiresFilePurpose: false,
      requiresDestination: atHome,
      destinations,
      filePurposes: this.filePurposes || [],
      selectedFilePurposeId: resolvedPurpose?.file_purpose_id,
      // Always pass folder code so the modal can resolve purpose after lookups load
      selectedDestinationCode: atHome ? undefined : folderContext?.code,
      // When already inside a folder and files are queued, upload once purpose is known.
      autoStartUpload: !atHome && queuedFiles.length > 0,
      queuedFiles,
    };

    const dialogRef = this._dialog.open(FileUploaderModalComponent, {
      data: { ...dialogData },
      width: '420px',
      maxWidth: '95vw',
      autoFocus: 'dialog',
      restoreFocus: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshRequested.emit();
      }
    });
  }

  private isFileDrag(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    if (!types || (types as any).length === 0) {
      // Some browsers omit types mid-drag; allow and let drop validate files
      return true;
    }
    // DOMStringList in some browsers; array-like in others
    return Array.from(types as any).includes('Files');
  }

  /**
   * True when the pointer has actually left the drop zone (not just moved
   * between children). Uses relatedTarget first, then client coordinates.
   */
  private isLeavingDropZone(event: DragEvent): boolean {
    const current = event.currentTarget as HTMLElement | null;
    if (!current) {
      return true;
    }

    const related = event.relatedTarget as Node | null;
    if (related && current.contains(related)) {
      return false;
    }

    // relatedTarget is often null during file drags; use pointer position
    if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
      const rect = current.getBoundingClientRect();
      const { clientX: x, clientY: y } = event;
      // Some browsers report 0,0 when leaving the window
      if (x === 0 && y === 0) {
        return true;
      }
      if (x > rect.left && x < rect.right && y > rect.top && y < rect.bottom) {
        return false;
      }
    }

    return true;
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canManageFiles || !this.isFileDrag(event)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDragging = true;
  }

  onDragOver(event: DragEvent): void {
    if (!this.canManageFiles || !this.isFileDrag(event)) {
      return;
    }
    // Required so the browser fires `drop` instead of navigating away
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    if (!this.isDragging) {
      this.isDragging = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canManageFiles) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.isLeavingDropZone(event)) {
      this.isDragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    if (!this.canManageFiles) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.openUploadDialog(files);
    }
  }

  onFileSelected(event: any): void {
    if (!this.canManageFiles) return;
    const files = event.target.files;
    if (files && files.length > 0) {
      this.openUploadDialog(files);
      event.target.value = '';
    }
  }

  triggerBrowse(fileInput: HTMLInputElement): void {
    if (!this.canManageFiles) return;
    fileInput.click();
  }

  onMainAreaClick(event: MouseEvent, fileInput: HTMLInputElement): void {
    if (!this.canManageFiles) return;
    // Only trigger if clicking on the background (empty space) or drop-hint chrome
    const target = event.target as HTMLElement;
    const isBackground =
      target.classList.contains('cipx-scrollable-content') ||
      target.classList.contains('file-cabinet-content') ||
      target.classList.contains('file-cabinet-tree') ||
      target.classList.contains('file-tree') ||
      target.classList.contains('file-cabinet-empty') ||
      target.classList.contains('file-cabinet-items') ||
      target.classList.contains('file-cabinet-folder-grid') ||
      target.classList.contains('file-cabinet-file-grid') ||
      target.classList.contains('file-cabinet-drop-hint') ||
      target.classList.contains('file-cabinet-drop-hint-title') ||
      target.classList.contains('file-cabinet-drop-hint-subtitle') ||
      target.classList.contains('file-cabinet-drop-hint-icon') ||
      target.classList.contains('flex-auto') ||
      target.classList.contains('p-2') ||
      target.classList.contains('p-3') ||
      target.classList.contains('p-4') ||
      target.classList.contains('p-6') ||
      target.classList.contains('p-8') ||
      target.classList.contains('space-y-6') ||
      target.classList.contains('space-y-8') ||
      target.classList.contains('flex-wrap') ||
      target.classList.contains('bg-gray-100');

    if (isBackground) {
      this.triggerBrowse(fileInput);
    }
  }
}
